<template>
	<Teleport to="body">
		<transition name="rte-media-dialog-fade">
			<div
				v-if="open"
				class="rte-media-dialog"
			>
				<div
					class="rte-media-dialog__panel"
					role="dialog"
					aria-modal="true"
					aria-labelledby="rte-media-dialog-title"
				>
					<div class="rte-media-dialog__header">
						<div>
							<div class="rte-media-dialog__eyebrow">Мультимедиа</div>
							<h3 id="rte-media-dialog-title" class="rte-media-dialog__title">
								Видео для статьи
							</h3>
							<p class="rte-media-dialog__subtitle">
								Вставьте ссылку на YouTube/Vimeo или загрузите своё видео с
								обложкой.
							</p>
						</div>
						<button
							type="button"
							class="rte-media-dialog__close"
							@click="handleClose"
							aria-label="Закрыть"
						>
							×
						</button>
					</div>

					<div class="rte-media-dialog__tabs">
						<button
							type="button"
							class="rte-media-dialog__tab"
							:class="{ 'is-active': mode === 'url' }"
							@click="mode = 'url'"
						>
							Ссылка
						</button>
						<button
							type="button"
							class="rte-media-dialog__tab"
							:class="{ 'is-active': mode === 'upload' }"
							@click="mode = 'upload'"
						>
							Файл
						</button>
					</div>

					<div class="rte-media-dialog__body">
						<div v-if="mode === 'url'" class="rte-media-dialog__section">
							<label class="rte-media-dialog__label" for="rte-media-url">
								URL видео
							</label>
							<input
								id="rte-media-url"
								v-model.trim="externalUrl"
								type="url"
								class="rte-media-dialog__input"
								placeholder="https://www.youtube.com/watch?v=..."
							/>
							<p class="rte-media-dialog__hint">
								Поддерживаются YouTube, Vimeo и прямые ссылки на видеофайлы.
							</p>
						</div>

						<div v-else class="rte-media-dialog__section">
							<div v-if="!canUploadFiles" class="rte-media-dialog__locked">
								<div class="rte-media-dialog__locked-title">
									Сначала сохраните урок
								</div>
								<p class="rte-media-dialog__locked-text">
									Загрузка файлов доступна только для уже созданного урока.
								</p>
							</div>

							<template v-else>
								<div class="rte-media-dialog__grid">
									<div class="rte-media-dialog__field">
										<label class="rte-media-dialog__label" for="rte-media-video">
											Видео
										</label>
										<input
											id="rte-media-video"
											ref="videoInput"
											type="file"
											accept="video/*"
											class="rte-media-dialog__file"
											@change="handleVideoFileChange"
										/>
										<div class="rte-media-dialog__file-meta">
											{{ videoFile ? videoFile.name : 'Файл не выбран' }}
										</div>
										<p class="rte-media-dialog__hint">
											До 100 МБ. Форматы: MP4, WebM, OGG, MOV и другие видео.
										</p>
									</div>

									<div class="rte-media-dialog__field">
										<label class="rte-media-dialog__label" for="rte-media-cover">
											Обложка
										</label>
										<input
											id="rte-media-cover"
											ref="coverInput"
											type="file"
											accept="image/*"
											class="rte-media-dialog__file"
											@change="handleCoverFileChange"
										/>
										<div class="rte-media-dialog__file-meta">
											{{ coverFile ? coverFile.name : 'Можно оставить пустым' }}
										</div>
										<p class="rte-media-dialog__hint">
											До 10 МБ. JPG, PNG, WebP и другие изображения.
										</p>
									</div>
								</div>
							</template>
						</div>

						<div class="rte-media-dialog__section">
							<label class="rte-media-dialog__label" for="rte-media-caption">
								Подпись
							</label>
							<input
								id="rte-media-caption"
								v-model.trim="caption"
								type="text"
								class="rte-media-dialog__input"
								placeholder="Например: Разбор станка в работе"
							/>
						</div>

						<div v-if="previewConfig" class="rte-media-dialog__preview">
							<div class="rte-media-dialog__preview-header">
								<span>Предпросмотр</span>
								<span class="rte-media-dialog__preview-kind">
									{{ previewKindLabel }}
								</span>
							</div>

							<div
								v-if="previewConfig.kind === 'external'"
								class="rte-media-dialog__iframe-shell"
							>
								<iframe
									:src="previewConfig.src"
									title="Предпросмотр видео"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowfullscreen
									loading="lazy"
								></iframe>
							</div>

							<video
								v-else
								class="rte-media-dialog__video-preview"
								:src="previewConfig.src"
								:poster="previewConfig.poster || undefined"
								controls
								preload="metadata"
								playsinline
							></video>

							<div class="rte-media-dialog__preview-caption">
								{{ resolvedCaption }}
							</div>
						</div>

						<p v-if="errorMessage" class="rte-media-dialog__error">
							{{ errorMessage }}
						</p>
					</div>

					<div class="rte-media-dialog__footer">
						<button
							type="button"
							class="rte-media-dialog__ghost"
							@click="handleClose"
						>
							Отмена
						</button>
						<button
							type="button"
							class="rte-media-dialog__primary"
							:disabled="isSubmitting"
							@click="handleSubmit"
						>
							{{ isSubmitting ? 'Загружаю...' : 'Вставить в статью' }}
						</button>
					</div>
				</div>
			</div>
		</transition>
	</Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import lessonsService from '@/services/lessons'

const VIDEO_UPLOAD_MAX_BYTES = 100 * 1024 * 1024
const COVER_UPLOAD_MAX_BYTES = 10 * 1024 * 1024
const DIRECT_VIDEO_PATTERN = /\.(mp4|webm|ogg|mov|m4v)(?:\?.*)?$/i

const props = defineProps({
	open: {
		type: Boolean,
		default: false,
	},
	lessonId: {
		type: [String, Number],
		default: null,
	},
})

const emit = defineEmits(['close', 'insert'])

const mode = ref('url')
const externalUrl = ref('')
const caption = ref('')
const videoFile = ref(null)
const coverFile = ref(null)
const errorMessage = ref('')
const isSubmitting = ref(false)
const videoPreviewUrl = ref('')
const coverPreviewUrl = ref('')

const videoInput = ref(null)
const coverInput = ref(null)
let previousBodyOverflow = ''
let previousBodyPaddingRight = ''

const canUploadFiles = computed(() =>
	props.lessonId !== null &&
	props.lessonId !== undefined &&
	String(props.lessonId).trim() !== ''
)

const normalizeUrl = value => String(value || '').trim()

const extractYouTubeId = url => {
	const match =
		url.match(/[?&]v=([^&]+)/i) ||
		url.match(/youtu\.be\/([^?&/]+)/i) ||
		url.match(/youtube\.com\/shorts\/([^?&/]+)/i) ||
		url.match(/youtube\.com\/embed\/([^?&/]+)/i)
	return match?.[1] || ''
}

const extractVimeoId = url => {
	const match =
		url.match(/vimeo\.com\/(?:video\/)?(\d+)/i) || []
	return match[1] || ''
}

const resolveExternalMedia = url => {
	const normalized = normalizeUrl(url)
	if (!normalized) return null

	if (/youtu(?:\.be|be\.com)/i.test(normalized)) {
		const videoId = extractYouTubeId(normalized)
		if (!videoId) return null
		return {
			kind: 'external',
			provider: 'youtube',
			src: `https://www.youtube-nocookie.com/embed/${videoId}`,
			watchUrl: `https://www.youtube.com/watch?v=${videoId}`,
		}
	}

	if (/vimeo\.com/i.test(normalized)) {
		const videoId = extractVimeoId(normalized)
		if (!videoId) return null
		return {
			kind: 'external',
			provider: 'vimeo',
			src: `https://player.vimeo.com/video/${videoId}`,
			watchUrl: `https://vimeo.com/${videoId}`,
		}
	}

	if (
		DIRECT_VIDEO_PATTERN.test(normalized) ||
		normalized.includes('/uploads/videos/')
	) {
		return {
			kind: 'video',
			src: normalized,
		}
	}

	return null
}

const resolvedCaption = computed(() => {
	if (caption.value) return caption.value
	if (mode.value === 'upload' && videoFile.value) {
		return videoFile.value.name.replace(/\.[^.]+$/, '')
	}
	const normalizedUrl = normalizeUrl(externalUrl.value)
	if (!normalizedUrl) return 'Видео'
	return normalizedUrl
})

const previewConfig = computed(() => {
	if (mode.value === 'upload' && videoPreviewUrl.value) {
		return {
			kind: 'video',
			src: videoPreviewUrl.value,
			poster: coverPreviewUrl.value || '',
		}
	}

	if (mode.value === 'url') {
		return resolveExternalMedia(externalUrl.value)
	}

	return null
})

const previewKindLabel = computed(() => {
	if (!previewConfig.value) return ''
	return previewConfig.value.kind === 'external' ? 'YouTube / Vimeo' : 'Локальное видео'
})

const revokeObjectUrl = value => {
	if (value && value.startsWith('blob:')) {
		URL.revokeObjectURL(value)
	}
}

const resetFiles = () => {
	videoFile.value = null
	coverFile.value = null
	revokeObjectUrl(videoPreviewUrl.value)
	revokeObjectUrl(coverPreviewUrl.value)
	videoPreviewUrl.value = ''
	coverPreviewUrl.value = ''
	if (videoInput.value) videoInput.value.value = ''
	if (coverInput.value) coverInput.value.value = ''
}

const resetState = () => {
	mode.value = 'url'
	externalUrl.value = ''
	caption.value = ''
	errorMessage.value = ''
	isSubmitting.value = false
	resetFiles()
}

const escapeHtml = value =>
	String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')

const _buildMediaCardShell = ({ title, body, footer = '' }) => `
<figure class="rte-video-card" data-rte-video-card="true">
	<div class="rte-video-card__header">
		<div class="rte-video-card__eyebrow">Видео</div>
		<div class="rte-video-card__title">${escapeHtml(title)}</div>
	</div>
	${body}
	${footer}
</figure>`

const buildExternalHtml = ({ watchUrl, provider: _provider, title: _title }) =>
	`<figure class="media"><oembed url="${escapeHtml(watchUrl)}"></oembed></figure>`

const buildMediaCaptionHtml = captionText => {
	const normalizedCaption = String(captionText || '').trim()
	if (!normalizedCaption) return ''
	return `<p class="rte-media-caption">${escapeHtml(normalizedCaption)}</p>`
}

const buildVideoOembedUrl = (src, poster) => {
	const normalizedSrc = String(src || '').trim()
	if (!normalizedSrc) return ''
	if (!poster) return normalizedSrc
	const joiner = normalizedSrc.includes('?') ? '&' : '?'
	return `${normalizedSrc}${joiner}poster=${encodeURIComponent(poster)}`
}

const buildVideoHtml = ({ src, poster, title: _title }) =>
	`<figure class="media"><oembed url="${escapeHtml(buildVideoOembedUrl(src, poster))}"></oembed></figure>`

const extractInlineUploadResponse = payload => {
	const data = payload?.data || payload || {}
	const assets = Array.isArray(data.assets) ? data.assets : []
	const videoAsset =
		assets.find(item => item.type === 'video') ||
		(data.videoUrl ? { url: data.videoUrl, type: 'video' } : null)
	const coverAsset =
		assets.find(item => item.type === 'image') ||
		(data.coverUrl ? { url: data.coverUrl, type: 'image' } : null)
	return {
		videoUrl: videoAsset?.url || '',
		coverUrl: coverAsset?.url || '',
	}
}

const handleClose = () => {
	if (isSubmitting.value) return
	resetState()
	emit('close')
}

const handleVideoFileChange = event => {
	errorMessage.value = ''
	const file = event.target.files?.[0] || null
	if (!file) return
	if (file.size > VIDEO_UPLOAD_MAX_BYTES) {
		errorMessage.value = 'Видео превышает лимит 100 МБ.'
		event.target.value = ''
		return
	}
	videoFile.value = file
	revokeObjectUrl(videoPreviewUrl.value)
	videoPreviewUrl.value = URL.createObjectURL(file)
	if (!caption.value) {
		caption.value = file.name.replace(/\.[^.]+$/, '')
	}
}

const handleCoverFileChange = event => {
	errorMessage.value = ''
	const file = event.target.files?.[0] || null
	if (!file) return
	if (file.size > COVER_UPLOAD_MAX_BYTES) {
		errorMessage.value = 'Обложка превышает лимит 10 МБ.'
		event.target.value = ''
		return
	}
	coverFile.value = file
	revokeObjectUrl(coverPreviewUrl.value)
	coverPreviewUrl.value = URL.createObjectURL(file)
}

const handleSubmit = async () => {
	errorMessage.value = ''

	if (mode.value === 'url') {
		const media = resolveExternalMedia(externalUrl.value)
		if (!media) {
			errorMessage.value = 'Введите корректную ссылку на видео.'
			return
		}
		const title = resolvedCaption.value || 'Видео'
		const html =
			media.kind === 'external'
				? buildExternalHtml({
					watchUrl: media.watchUrl,
					provider: media.provider,
					title,
				}) + buildMediaCaptionHtml(caption.value)
				: buildVideoHtml({ src: media.src, poster: '', title })
		emit('insert', html)
		handleClose()
		return
	}

	if (!canUploadFiles.value) {
		errorMessage.value = 'Сначала сохраните урок, потом загружайте видео.'
		return
	}

	if (!videoFile.value) {
		errorMessage.value = 'Выберите видеофайл.'
		return
	}

	isSubmitting.value = true
	try {
		const response = await lessonsService.uploadInlineMedia(props.lessonId, {
			videoFile: videoFile.value,
			coverFile: coverFile.value,
		})
		const uploaded = extractInlineUploadResponse(response)
		if (!uploaded.videoUrl) {
			throw new Error('Сервер не вернул URL загруженного видео.')
		}

		const html = buildVideoHtml({
			src: uploaded.videoUrl,
			poster: uploaded.coverUrl,
			title: resolvedCaption.value || 'Видео',
		}) + buildMediaCaptionHtml(caption.value)
		emit('insert', html)
		handleClose()
	} catch (error) {
		errorMessage.value =
			error?.response?.data?.error?.message ||
			error?.response?.data?.message ||
			error?.message ||
			'Не удалось загрузить видео.'
	} finally {
		isSubmitting.value = false
	}
}

const lockBodyScroll = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') return
	const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
	previousBodyOverflow = document.body.style.overflow
	previousBodyPaddingRight = document.body.style.paddingRight
	document.body.style.overflow = 'hidden'
	if (scrollbarWidth > 0) {
		document.body.style.paddingRight = `${scrollbarWidth}px`
	}
}

const unlockBodyScroll = () => {
	if (typeof document === 'undefined') return
	document.body.style.overflow = previousBodyOverflow
	document.body.style.paddingRight = previousBodyPaddingRight
}

watch(
	() => props.open,
	isOpen => {
		if (isOpen) {
			lockBodyScroll()
			return
		}
		unlockBodyScroll()
	}
)

onBeforeUnmount(() => {
	unlockBodyScroll()
	resetFiles()
})
</script>

<style scoped>
.rte-media-dialog {
	position: fixed;
	inset: 0;
	z-index: 5000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(15, 23, 42, 0.56);
	backdrop-filter: blur(6px);
}

.rte-media-dialog__panel {
	width: min(760px, 100%);
	max-height: calc(100vh - 48px);
	overflow: auto;
	border-radius: 28px;
	background:
		radial-gradient(circle at top right, rgba(56, 189, 248, 0.18), transparent 28%),
		linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
	box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
}

.rte-media-dialog__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	padding: 28px 28px 18px;
	border-bottom: 1px solid rgba(148, 163, 184, 0.22);
}

.rte-media-dialog__eyebrow {
	margin-bottom: 8px;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: #0284c7;
}

.rte-media-dialog__title {
	margin: 0 0 8px;
	font-size: 28px;
	line-height: 1.1;
	color: #0f172a;
}

.rte-media-dialog__subtitle {
	margin: 0;
	font-size: 14px;
	line-height: 1.55;
	color: #475569;
}

.rte-media-dialog__close {
	flex-shrink: 0;
	width: 42px;
	height: 42px;
	border: none;
	border-radius: 999px;
	background: #e2e8f0;
	color: #0f172a;
	font-size: 24px;
	line-height: 1;
	cursor: pointer;
}

.rte-media-dialog__tabs {
	display: flex;
	gap: 10px;
	padding: 18px 28px 0;
}

.rte-media-dialog__tab {
	padding: 10px 16px;
	border: 1px solid #cbd5e1;
	border-radius: 999px;
	background: #ffffff;
	color: #334155;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
}

.rte-media-dialog__tab.is-active {
	border-color: #0ea5e9;
	background: #e0f2fe;
	color: #0369a1;
}

.rte-media-dialog__body {
	padding: 24px 28px 18px;
}

.rte-media-dialog__section + .rte-media-dialog__section {
	margin-top: 20px;
}

.rte-media-dialog__grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
}

.rte-media-dialog__field {
	padding: 16px;
	border: 1px solid #e2e8f0;
	border-radius: 18px;
	background: #ffffff;
}

.rte-media-dialog__label {
	display: block;
	margin-bottom: 8px;
	font-size: 14px;
	font-weight: 600;
	color: #0f172a;
}

.rte-media-dialog__input,
.rte-media-dialog__file {
	width: 100%;
	padding: 12px 14px;
	border: 1px solid #cbd5e1;
	border-radius: 14px;
	background: #ffffff;
	font-size: 14px;
	color: #0f172a;
}

.rte-media-dialog__file {
	padding: 10px 12px;
}

.rte-media-dialog__file-meta {
	margin-top: 10px;
	font-size: 13px;
	color: #334155;
}

.rte-media-dialog__hint {
	margin: 8px 0 0;
	font-size: 12px;
	line-height: 1.5;
	color: #64748b;
}

.rte-media-dialog__locked {
	padding: 18px;
	border: 1px dashed #cbd5e1;
	border-radius: 18px;
	background: #f8fafc;
}

.rte-media-dialog__locked-title {
	margin-bottom: 6px;
	font-size: 16px;
	font-weight: 700;
	color: #0f172a;
}

.rte-media-dialog__locked-text {
	margin: 0;
	font-size: 14px;
	line-height: 1.5;
	color: #475569;
}

.rte-media-dialog__preview {
	margin-top: 24px;
	padding: 18px;
	border-radius: 24px;
	background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
	box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.18);
}

.rte-media-dialog__preview-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 14px;
	font-size: 13px;
	font-weight: 600;
	color: #cbd5e1;
}

.rte-media-dialog__preview-kind {
	padding: 6px 10px;
	border-radius: 999px;
	background: rgba(14, 165, 233, 0.16);
	color: #bae6fd;
}

.rte-media-dialog__iframe-shell {
	position: relative;
	overflow: hidden;
	padding-top: 56.25%;
	border-radius: 18px;
	background: #020617;
}

.rte-media-dialog__iframe-shell iframe {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	border: 0;
}

.rte-media-dialog__video-preview {
	display: block;
	width: 100%;
	border-radius: 18px;
	background: #020617;
}

.rte-media-dialog__preview-caption {
	margin-top: 12px;
	font-size: 14px;
	font-weight: 600;
	color: #f8fafc;
}

.rte-media-dialog__error {
	margin: 18px 0 0;
	padding: 12px 14px;
	border-radius: 14px;
	background: #fef2f2;
	color: #b91c1c;
	font-size: 14px;
	line-height: 1.45;
}

.rte-media-dialog__footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	padding: 0 28px 28px;
}

.rte-media-dialog__ghost,
.rte-media-dialog__primary {
	padding: 12px 18px;
	border-radius: 14px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
}

.rte-media-dialog__ghost {
	border: 1px solid #cbd5e1;
	background: #ffffff;
	color: #334155;
	display: none;
}

.rte-media-dialog__primary {
	border: none;
	background: linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%);
	color: #ffffff;
	box-shadow: 0 14px 30px rgba(14, 165, 233, 0.24);
}

.rte-media-dialog__primary:disabled {
	cursor: wait;
	opacity: 0.75;
}

:global(.rte-media-caption) {
	margin: 10px 0 18px;
	font-size: 14px;
	line-height: 1.5;
	color: #475569;
	text-align: center;
}

.rte-media-dialog-fade-enter-active,
.rte-media-dialog-fade-leave-active {
	transition: opacity 0.2s ease;
}

.rte-media-dialog-fade-enter-from,
.rte-media-dialog-fade-leave-to {
	opacity: 0;
}

@media (max-width: 720px) {
	.rte-media-dialog {
		padding: 12px;
	}

	.rte-media-dialog__panel {
		border-radius: 22px;
		max-height: calc(100vh - 24px);
	}

	.rte-media-dialog__header,
	.rte-media-dialog__body,
	.rte-media-dialog__footer {
		padding-left: 18px;
		padding-right: 18px;
	}

	.rte-media-dialog__tabs {
		padding-left: 18px;
		padding-right: 18px;
	}

	.rte-media-dialog__grid {
		grid-template-columns: 1fr;
	}

	.rte-media-dialog__footer {
		flex-direction: column-reverse;
		align-items: stretch;
	}

	.rte-media-dialog__ghost,
	.rte-media-dialog__primary {
		width: 100%;
	}
}
</style>
